/**
 * CacheManager - Client-side caching utility for HiBFF
 * Caches AvA config, user context, and conversation data
 * to reduce latency on repeated loads and speed up AvA interactions.
 */

const CACHE_PREFIX = 'hibff_cache_';
const DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes

const CacheManager = {
  /**
   * Store a value with optional TTL (time-to-live in ms)
   */
  set(key, value, ttl = DEFAULT_TTL) {
    try {
      const entry = {
        data: value,
        ts: Date.now(),
        ttl,
      };
      sessionStorage.setItem(CACHE_PREFIX + key, JSON.stringify(entry));
    } catch {
      // Storage full — evict oldest entries
      this.evict();
    }
  },

  /**
   * Retrieve a cached value. Returns null if expired or missing.
   */
  get(key) {
    try {
      const raw = sessionStorage.getItem(CACHE_PREFIX + key);
      if (!raw) return null;
      const entry = JSON.parse(raw);
      if (Date.now() - entry.ts > entry.ttl) {
        sessionStorage.removeItem(CACHE_PREFIX + key);
        return null;
      }
      return entry.data;
    } catch {
      return null;
    }
  },

  /**
   * Remove a specific cache entry
   */
  remove(key) {
    sessionStorage.removeItem(CACHE_PREFIX + key);
  },

  /**
   * Clear all HiBFF cache entries
   */
  clear() {
    const keys = Object.keys(sessionStorage).filter(k => k.startsWith(CACHE_PREFIX));
    keys.forEach(k => sessionStorage.removeItem(k));
  },

  /**
   * Evict oldest cache entries when storage is full
   */
  evict() {
    const entries = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (k?.startsWith(CACHE_PREFIX)) {
        try {
          const entry = JSON.parse(sessionStorage.getItem(k));
          entries.push({ key: k, ts: entry.ts });
        } catch {
          sessionStorage.removeItem(k);
        }
      }
    }
    // Remove oldest 25%
    entries.sort((a, b) => a.ts - b.ts);
    const removeCount = Math.max(1, Math.floor(entries.length / 4));
    entries.slice(0, removeCount).forEach(e => sessionStorage.removeItem(e.key));
  },

  /**
   * Fetch-with-cache: wraps an API call with cache layer
   * @param {string} cacheKey - Unique cache key
   * @param {Function} fetchFn - Async function that returns data
   * @param {number} ttl - Cache TTL in ms
   */
  async fetchWithCache(cacheKey, fetchFn, ttl = DEFAULT_TTL) {
    const cached = this.get(cacheKey);
    if (cached) return cached;
    const data = await fetchFn();
    this.set(cacheKey, data, ttl);
    return data;
  },
};

export default CacheManager;
